import Button from 'components/Button';
import FooterBasic from 'components/Footers/Basic';
import FormActions from 'components/Form/FormActions';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import InputEmail from 'components/Form/InputEmail';
import InputPassword from 'components/Form/InputPassword';
import InputPhoneNumber from 'components/Form/InputPhoneNumber';
import InputSanitized from 'components/Form/InputSanitized';
import {usernameSanitizer} from 'components/Form/InputSanitized/utils';
import InputText from 'components/Form/InputText';
import SubmitButton from 'components/Form/SubmitButton';
import HeaderBasic from 'components/Headers/Basic';
import DualPage from 'components/Layout/DualPage';
import Stacked from 'components/Layout/Stacked';
import SectionHeader from 'components/SectionHeader';
import {H1} from 'components/Typography';
import {FC, useEffect, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {useLocale} from 'state/locale';
import {jt, t} from 'ttag';
import {Backend} from 'types/api';
import {onApiFormError} from 'utils/request/result';

import {SignupBank, SignupDocument, SignupSend} from '../assets/svg';
import {UrlParameterOptions, UserDetailsFormFields} from '../types';
import {usePostUserDetails, UserDetailsFormData} from './services';

export type UserDetailsBackend = Backend<UrlParameterOptions>;

type UserDetailsProps = {
    onSuccess: () => void;
    onUpdate: (formData: Partial<UserDetailsFormFields>) => void;
    prefillData?: Partial<UserDetailsFormFields>;
};

const UserDetails: FC<UserDetailsProps> = ({
    onSuccess,
    onUpdate,
    prefillData = {},
}) => {
    const {data} = useBackend<UserDetailsBackend>();

    const methods = useForm<UserDetailsFormData>({
        defaultValues: {
            email: prefillData.email,
            nameFirst: prefillData.nameFirst,
            nameLast: prefillData.nameLast,
            password: prefillData.password,
            phoneNumber: prefillData.phoneNumber,
            regcode: prefillData.regcode,
            username: prefillData.username,
        },
    });
    const {handleSubmit, watch} = methods;

    const [showRegistrationCode, setRegistrationCode] = useState(
        () => !!prefillData.regcode,
    );

    const {mutateAsync} = usePostUserDetails();
    const languageCode = useLocale();

    useEffect(() => {
        const subscription = watch((value) => {
            onUpdate(value);
        });

        return () => subscription.unsubscribe();
    });

    const onSubmit = async (formData: UserDetailsFormData) => {
        const submittedData = {
            ...formData,
            languageCode,
            leadSource: data.leadSource,
            utmCampaign: data.utmCampaign,
            utmContent: data.utmContent,
            utmMedium: data.utmMedium,
            utmSource: data.utmSource,
            utmTerm: data.utmTerm,
        };
        mutateAsync(submittedData)
            .then(() => {
                onSuccess();
            })
            .catch(onApiFormError(methods));
    };

    const enterItNowSpan = (
        <span
            className="text-green-700"
            key="enter-it-now"
        >{t`Enter it now`}</span>
    );

    return (
        <div className="relative flex min-h-screen flex-col bg-grey-100 ">
            <HeaderBasic className="px-8" />
            <DualPage
                pagination={{
                    current: 1,
                    length: 2,
                }}
                size="6xl"
            >
                <FormProvider {...methods}>
                    <form
                        className="rounded border border-grey-300 bg-white p-6"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <FormHeader
                            className="mt-0"
                            hideHR={true}
                            subtitle={t`30-day trial of our features. Your free trial will not automatically become a paid subscription.`}
                            title={t`Create Your Free Account`}
                        />
                        <Stacked className="mt-4 w-full">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <InputText
                                    className="w-full"
                                    label={t`Last name`}
                                    name="nameLast"
                                    required={true}
                                />
                                <InputText
                                    className="w-full"
                                    label={t`First name`}
                                    name="nameFirst"
                                    required={true}
                                />
                            </div>
                            <InputEmail
                                label={t`Work email`}
                                name="email"
                                required={true}
                            />
                            <InputPhoneNumber
                                helpText={t`e.g. 000-0000-0000`}
                                label={t`Phone number`}
                                name="phoneNumber"
                                required={true}
                            />
                            <InputSanitized
                                helpText={t`Choose a username that contains only letters, numbers, and ,/+/-/_. This is for login only.`}
                                label={t`Username`}
                                name="username"
                                required={true}
                                sanitizer={usernameSanitizer}
                            />
                            <InputPassword
                                name="password"
                                required={true}
                                showPasswordRules={true}
                            />
                            {showRegistrationCode ? (
                                <InputText
                                    label={t`Registration Code`}
                                    name="regcode"
                                />
                            ) : (
                                <Button
                                    aria-label="Registration code button"
                                    className="whitespace-pre-line text-left"
                                    kind="tertiary"
                                    onClick={() => setRegistrationCode(true)}
                                >
                                    <span className="text-light-primary">
                                        {jt`Do you have a registration code? ${enterItNowSpan}`}
                                    </span>
                                </Button>
                            )}
                            <FormResult />
                            <FormActions>
                                <SubmitButton>
                                    {t`Let's get started`}
                                </SubmitButton>
                            </FormActions>
                        </Stacked>
                    </form>
                </FormProvider>
                <div className="hidden p-6 md:grid">
                    <SectionHeader
                        className="mt-0"
                        hideHR={true}
                        title={t`Upgrade Your Invoicing Operations`}
                    />
                    <section className="mt-6 flex">
                        <SignupDocument className="w-40 flex-none" />
                        <div>
                            <H1 className="mt-0">{t`Bilingual Support`}</H1>
                            <p>{t`Document template in both Japanese and English is available. Our full bilingual support team is happy to support.`}</p>
                        </div>
                    </section>
                    <section className="mt-4 flex">
                        <SignupSend className="w-40 flex-none" />
                        <div>
                            <H1 className="mt-0">
                                {t`Send Documents with One Click`}
                            </H1>
                            <p>{t`Send your invoices via our Secure Send. For Japan, even through the post could be handled by us.`}</p>
                        </div>
                    </section>
                    <section className="mt-4 flex">
                        <SignupBank className="w-40 flex-none" />
                        <div>
                            <H1 className="mt-0">
                                {t`One Place Payment Management`}
                            </H1>
                            <p>{t`Our bankbook sync and machine learning algorithm will give you the simplest way to keep track of your payment receivables.`}</p>
                        </div>
                    </section>
                </div>
            </DualPage>
            <FooterBasic />
        </div>
    );
};

export default UserDetails;
